import { useEffect } from 'react'
import { setGuidedReturn } from '../../redux/slices/checkout'
import useAppDispatch from '../useAppDispatch'
import useAppStore from '../useAppStore'

/**
 * Ends any guided walk, because the shopper is no longer inside one.
 *
 * `checkoutSlice.guidedReturnPath` is set before the first picker is pushed,
 * by the summary CTA or by COM-1501's `startGuidedWalk` (a screen's
 * `runOnLoad`, the catalog's on `mondelez-mx-b2b-ng`), and read by the picker
 * screens' shared `confirm()`. This is what guarantees it is gone everywhere
 * else.
 *
 * **Mounted on the three screens a walk can end on** —
 * `templates/Summary.tsx`, `templates/Cart.tsx` and `templates/Catalog.tsx`,
 * the last because `startGuidedWalk` returns there. Arriving at any of them
 * means the walk is over, however the shopper got there: the walk's own final
 * hop, the picker's Back control, the webview's back gesture, or an authored
 * link.
 *
 * ⚠️ **The cart mount is the load-bearing one, and it is easy to think
 * redundant.** COM-1045's *Cambiar* row and COM-1050's recovery redirect both
 * push into a picker **from the cart**, and both rely on `guidedReturnPath`
 * being absent so that `confirm()` returns the shopper to the cart. Clear only
 * on the summary and that holds for every path *through* the summary — but a
 * shopper who leaves a picker straight to the cart keeps a stale return path,
 * and their next *Cambiar* confirm routes them into the other picker or onto a
 * summary they never asked for. Clearing here makes `confirm()`'s "absence
 * means they came from the cart" literally true rather than contingent on
 * which screens the shopper happened to pass through.
 *
 * Deliberately **not** called from the pickers' own `back`, which writes
 * nothing by design: a shopper mid-walk who backs onto an *earlier picker* is
 * still in the walk, and clearing there would drop them out of it. Ending the
 * walk is about arriving somewhere that is not a picker, not about leaving one.
 *
 * Mount-only. Re-running it on every render would fight the summary CTA, which
 * writes the field and then navigates away within the same interaction.
 */
export const useEndGuidedWalk = (): void => {
  const dispatch = useAppDispatch()
  const store = useAppStore()

  useEffect(() => {
    /*
     * Nothing to end, nothing dispatched (COM-1501). `templates/Catalog.tsx`
     * mounts this on every storefront's catalog, and a storefront that never
     * walks anyone should see no new action on it.
     */
    if (store.getState().checkoutSlice?.guidedReturnPath === undefined) {
      return
    }

    dispatch(setGuidedReturn(undefined))
  }, [dispatch, store])
}

export default useEndGuidedWalk
